// A link may open the review on the puzzle of one of the reader's moves,
// `?puzzle=<1-based ply>` (Home's practice card): it starts once the game and
// its scan are loaded, then leaves the address so a reload does not start it
// again. The address is read and cleared through what the caller passes, so
// this module never touches `window`.
import type { ReviewState } from '@chess/api-review';

/** The 1-based ply a `?puzzle=` asks for, when it is a positive whole number. */
export function puzzleOfSearch(search: string): number | undefined {
  const asked = Number(new URLSearchParams(search).get('puzzle'));
  return Number.isInteger(asked) && asked > 0 ? asked : undefined;
}

/** An address without its `puzzle` parameter, as a path with its query and hash. */
export function withoutPuzzle(href: string): string {
  const url = new URL(href);
  url.searchParams.delete('puzzle');
  return url.pathname + url.search + url.hash;
}

export interface AddressPuzzleDeps {
  /** The address's query, `?…`. */
  search(): string;
  /** Takes `puzzle` out of the address. */
  clear(): void;
  /** Starts the puzzle of the reader's move at a 1-based ply. */
  start(ply: number): void;
}

export function createAddressPuzzle(deps: AddressPuzzleDeps) {
  let wanted: { gameId: string; ply: number } | undefined;

  /** Starts the asked puzzle once its game, scan and reader are in, once only. */
  const follow = (state: ReviewState): void => {
    if (!wanted || wanted.gameId !== state.gameId || !state.game || !state.scan?.scan || !state.reader) return;
    const { ply } = wanted;
    wanted = undefined;
    deps.clear();
    deps.start(ply);
  };

  return {
    /** Another game is shown: what its address asks for replaces anything asked before. */
    opened(state: ReviewState): void {
      const ply = puzzleOfSearch(deps.search());
      wanted = state.gameId && ply ? { gameId: state.gameId, ply } : undefined;
      follow(state);
    },
    follow,
  };
}
